import {
  useMutation,
  useQuery,
  type QueryClient,
  type UseMutationOptions,
  type UseQueryOptions,
} from '@tanstack/react-query';
import type { Envelope } from './types';

export const ADMIN_BASE = '/admin-api';

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: number | null = null,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

type UnauthorizedListener = () => void;
const unauthorizedListeners = new Set<UnauthorizedListener>();

/** The session cookie is httpOnly, so a 401 is the only signal that it is gone. */
export function onUnauthorized(listener: UnauthorizedListener): () => void {
  unauthorizedListeners.add(listener);
  return () => unauthorizedListeners.delete(listener);
}

function notifyUnauthorized(): void {
  for (const listener of [...unauthorizedListeners]) listener();
}

export type QueryParams = Record<
  string,
  string | number | boolean | null | undefined
>;

export function qs(params: QueryParams): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    search.set(key, String(value));
  }
  const out = search.toString();
  return out ? `?${out}` : '';
}

type RequestOptions = {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
};

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const url = path.startsWith('/admin-api') || path.startsWith('/api') ? path : `${ADMIN_BASE}${path}`;
  const isLogin = url.endsWith('/auth/login');

  let response: Response;
  try {
    response = await fetch(url, {
      method: options.method ?? 'GET',
      credentials: 'same-origin',
      headers: options.body === undefined ? undefined : { 'content-type': 'application/json' },
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      signal: options.signal,
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    throw new ApiError('Сервер недоступен. Проверьте, запущен ли API.', 0);
  }

  if (response.status === 401 && !isLogin) {
    notifyUnauthorized();
    throw new ApiError('Сессия истекла, войдите заново.', 401, 401);
  }

  let payload: Envelope<T> | null = null;
  const raw = await response.text();
  if (raw) {
    try {
      payload = JSON.parse(raw) as Envelope<T>;
    } catch {
      payload = null;
    }
  }

  if (!payload) {
    if (response.ok) return undefined as T;
    throw new ApiError(`Ошибка HTTP ${response.status}`, response.status);
  }

  if (!response.ok || payload.code !== 1) {
    throw new ApiError(
      payload.msg || `Ошибка HTTP ${response.status}`,
      response.status,
      payload.code ?? null,
    );
  }

  return payload.data;
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) => request<T>(path, { signal }),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  del: <T>(path: string, body?: unknown) => request<T>(path, { method: 'DELETE', body }),
};

export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return 'Неизвестная ошибка';
}

type ApiQueryOptions<T> = Omit<UseQueryOptions<T, ApiError, T, readonly unknown[]>, 'queryKey' | 'queryFn'>;

/** Query keyed by the request path, so mutations can invalidate by prefix. */
export function useApi<T>(path: string | null, options: ApiQueryOptions<T> = {}) {
  return useQuery<T, ApiError, T, readonly unknown[]>({
    queryKey: ['api', path],
    queryFn: ({ signal }) => api.get<T>(path as string, signal),
    enabled: path !== null && (options.enabled ?? true),
    ...options,
  });
}

export function useApiMutation<TData, TVars>(
  fn: (vars: TVars) => Promise<TData>,
  options: Omit<UseMutationOptions<TData, ApiError, TVars>, 'mutationFn'> = {},
) {
  return useMutation<TData, ApiError, TVars>({ mutationFn: fn, ...options });
}

export const apiKeyOf = (path: string): readonly unknown[] => ['api', path];

/** Query keys carry the full path with its query string, so refresh by prefix. */
export function invalidatePrefix(client: QueryClient, prefix: string): Promise<void> {
  return client.invalidateQueries({
    predicate: (query) => {
      const key = query.queryKey[1];
      return typeof key === 'string' && key.startsWith(prefix);
    },
  });
}
